import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';

// CustomSearchableDropdownTickmark with its options grouped under a heading per
// zone (RON-5769). Spaces from several zones arrive in one list, each carrying
// its ZoneId / ZoneName; a heading is built only from the spaces present, so a
// zone with no spaces gets none. Space names can repeat across zones, so React
// keys combine the group and the value.
function CustomZoneWiseSpotDropdownTickmark({
  items = [],
  value,
  labelKey,
  valueKey,
  groupKey = "ZoneId",
  groupLabelKey = "ZoneName",
  placeholder = "Select",
  searchPlaceholder = "Search",
  noOptionsMessage = "",
  onChange,
  searchable = true,
  disabled = false,
  error = false,
  rootClassName = ""
}) {
  const { t } = useTranslation("common");

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [touched, setTouched] = useState(false); // track if user changed value
  const dropdownRef = useRef(null);

  // store initial value only once
  const initialValueRef = useRef(value);

  const selectedItem = items.find(
    (item) => String(item[valueKey]) === String(value)
  ) || null;

  // reset touched if items change
  useEffect(() => {
    initialValueRef.current = value; // keep track of new initial value
    setTouched(false); // reset touched
  }, [items]);

  // filtered items for search
  const filteredItems = useMemo(() => {
    if (!searchable) return items;
    return items.filter((item) =>
      item[labelKey]
        ?.toString()
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [items, searchText, searchable, labelKey]);

  // groups in the order the API returned them
  const groups = useMemo(() => {
    const byGroup = new Map();
    filteredItems.forEach((item) => {
      const groupId = String(item[groupKey] ?? "");
      if (!byGroup.has(groupId)) {
        byGroup.set(groupId, { id: groupId, label: item[groupLabelKey] || "", items: [] });
      }
      byGroup.get(groupId).items.push(item);
    });
    return [...byGroup.values()];
  }, [filteredItems, groupKey, groupLabelKey]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    // For single-option dropdown, always mark as touched on click
    const isSingleOption = items.length === 1;

    if (isSingleOption || String(item[valueKey]) !== String(initialValueRef.current)) {
      setTouched(true);
    }

    onChange(item[valueKey]);
    setIsOpen(false);
    setSearchText("");
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      setSearchText("");
    }
  };

  return (
    <div className={`${rootClassName} ctm-dropdown-inner d-block position-relative`.trim()} ref={dropdownRef}>
      {/* ctm-success -> green border and text, only once the user has changed the value.
          error-form  -> red border when the field is invalid / required. */}
      <a
        className={[
          "ctm-dropdown",
          isOpen ? "dropdown-open" : "",
          disabled ? "ctm-dropdown-disabled" : "",
          touched ? "ctm-success" : "",
          error ? "error-form" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={toggleDropdown}
      >
        {selectedItem ? selectedItem[labelKey] : placeholder}
      </a>

      {isOpen && !disabled && (
        <div className="ctm-dropdown-panel">
          {searchable && (
            <div className="px-3 pt-3 pb-2">
              <input
                type="text"
                className="form-control"
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <ul className="ctm-dropdown-menu ctm-dropdown-check">
            {groups.length > 0 ? (
              groups.map((group) => (
                <React.Fragment key={`group-${group.id}`}>
                  <li className="ctm-dropdown-group-label" role="presentation">
                    {group.label}
                  </li>
                  {group.items.map((item) => (
                    <li
                      key={`${group.id}-${item[valueKey]}`}
                      onClick={() => handleSelect(item)}
                      className={String(value) === String(item[valueKey]) ? "ctm-selected-option" : ""}
                    >
                      <a>{item[labelKey]}</a>
                    </li>
                  ))}
                </React.Fragment>
              ))
            ) : (
              <div style={{ padding: "8px 12px", color: "#9ca3af" }}>
                {noOptionsMessage || t("react_no_options_found")}
              </div>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CustomZoneWiseSpotDropdownTickmark;
